/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';
import {loadModules} from 'esri-loader';

/**
 * @useClusterExtent
 *
 * Provides a hook for retrieving a cluster
 * extent based on given parameters.
 *
 * ```typescript
 *
 *    const getClusterExtent = useClusterExtent();
 *    ...
 *    Get the cluster extent based on given parameters
 *    const assets = useClusterExtent?.current(extent, spatialReference);
 *
 * ```
 */
export const useClusterExtent = () => {
  const getClusterExtent = useRef(null);
  useEffect(() => {
    loadModules(['esri/geometry/Extent'])
      .then(([Extent]) => {
        getClusterExtent.current = function(extent: any, spatialReference: __esri.SpatialReference): __esri.Extent {
          return getExtent(extent, spatialReference, Extent);
        };
      });
  }, []);
  return function(extent: any, spatialReference: __esri.SpatialReference): __esri.Extent {
    return getClusterExtent?.current ? getClusterExtent.current(extent, spatialReference) : null;
  };
};

/**
 * @setClusterExtent
 *
 * Provides a utility to get the Esri extent and 
 * execute the callback function
 *
 * ```typescript
 *
 *    const getClusterExtent = useClusterExtent();
 *    ...
 *    Get the cluster extent based on given parameters
 *    const assets = useClusterExtent?.current(extent, spatialReference);
 *
 * ```
 */
export const setClusterExtent = (extent: any, spatialReference: __esri.SpatialReference, callback: Function) => {
  const viewExtent = extent;
  loadModules(['esri/geometry/Extent']).then(([Extent]) => {
    const extent = getExtent(viewExtent, spatialReference, Extent);
    callback(extent);
  });
};

/**
 * @private
 * 
 * @getExtent
 * 
 * Provides a utility to create and returns esri extent object
 * based on given parameters.
 * 
 * @param extent  it's View's Extent
 * @param spatialReference  it's the spatial reference to use
 * @param ExtentLibrary it's Esri's Extent 
 * 
 */
function getExtent(extent: any, spatialReference: __esri.SpatialReference, ExtentLibrary: any): __esri.Extent {
  if (extent?.xmin) {
    return new ExtentLibrary(extent?.xmin, extent?.ymin, extent?.xmax, extent?.ymax, spatialReference);
  }
  return new ExtentLibrary(extent[0], extent[1], extent[2], extent[3], spatialReference);
};

export default useClusterExtent;
